<script setup lang="ts">
import PlusMenuLink from '@app/layout/nav/mobile/components/PlusMenuLink.vue';
import {
  URL_BUYER_INVOICES,
  URL_OFFER_CREATE,
  URL_SELECTION_ITEM_DASHBOARD,
  URL_USER_NOTIFICATIONS,
  URL_USER_ORGANIZATION_MANAGE,
  URL_USER_ORGANIZATION_PAYMENT_METHOD_BUYER,
  URL_USER_ORGANIZATION_TEAM,
  URL_USER_PROFILE_MANAGE,
  URL_USER_PROPOSAL,
} from '@tools/routes/routes';
import type { Submenu } from '@global-types/nav/menuItems';
import { IconHeartStraight, IconAlignTop, IconRent, IconUserCircle, IconBellRinging, IconPlus, IconBriefcase } from '@icons';
import { trans } from '@tools/translation.ts';
import Icon from '@components/Icon.vue';
import { storeToRefs } from 'pinia';
import { useNavbarStore } from '@stores/layout/useNavbarStore';
import Divider from '@components/Divider.vue';
import PlusMenuItemGroup from '@app/layout/nav/mobile/components/PlusMenuItemGroup.vue';
import MenuAccountManagerContact from '@app/layout/nav/MenuAccountManagerContact.vue';
import { useUserProfileStore } from '@stores/useUserProfileStore.ts';
import { MenuItemGroup as MenuItemGroupEnum } from '@enums/menuItemGroups.ts';
import { computed } from 'vue';

const navbarStore = useNavbarStore();
const { notifications } = storeToRefs(navbarStore);
const { isCurrentDropdownOpen, setCurrentDropdownOpen } = navbarStore;

const { hasPayBuyer } = storeToRefs(useUserProfileStore());

const subMenuOrganization = computed<Submenu[]>(() => {
  const items = [
    { name: trans('user_menu.organization'), link: URL_USER_ORGANIZATION_MANAGE },
    { name: trans('user_menu.team'), link: URL_USER_ORGANIZATION_TEAM },
  ];

  if (hasPayBuyer.value) {
    items.push(
      { name: trans('user_menu.gestion_parameters'), link: URL_USER_ORGANIZATION_PAYMENT_METHOD_BUYER },
    );
  }

  return items;
});
</script>

<template>
  <li>
    <PlusMenuLink
      :link="URL_USER_PROPOSAL"
      :has-pin="notifications.unread_match_proposal > 0"
    >
      <template #icon="{ isActive }">
        <Icon
          :svg="IconHeartStraight"
          width="24"
          height="24"
          :fill="isActive ? 'black' : 'gray-darker'"
        />
      </template>

      <template #text>
        {{ trans('user_menu.selection_item') }}

        <template v-if="notifications.unread_match_proposal > 0">
          ({{ notifications.unread_match_proposal }})
        </template>
      </template>
    </PlusMenuLink>
  </li>

  <li>
    <PlusMenuLink :link="URL_SELECTION_ITEM_DASHBOARD">
      <template #icon="{ isActive }">
        <Icon
          :svg="IconAlignTop"
          width="24"
          height="24"
          :fill="isActive ? 'black' : 'gray-darker'"
        />
      </template>

      <template #text>
        {{ trans('user_menu.deals') }}
      </template>
    </PlusMenuLink>
  </li>

  <li class="px-3">
    <Divider class="m-0" color="gray" />
  </li>

  <li>
    <PlusMenuItemGroup
      :is-dropdown-open="isCurrentDropdownOpen(MenuItemGroupEnum.MY_ORGANIZATION)"
      :submenus="subMenuOrganization"
      @toggle-dropdown="(isOpen) => setCurrentDropdownOpen(isOpen ? MenuItemGroupEnum.MY_ORGANIZATION : null)"
    >
      <template #icon="{ isActive }">
        <Icon
          :svg="IconBriefcase"
          width="24"
          height="24"
          :fill="isActive ? 'black' : 'gray-darker'"
        />
      </template>

      <template #text>
        {{ trans('user.menu.my_organization') }}
      </template>
    </PlusMenuItemGroup>
  </li>

  <li>
    <PlusMenuLink :link="URL_USER_PROFILE_MANAGE">
      <template #icon="{ isActive }">
        <Icon
          :svg="IconUserCircle"
          width="24"
          height="24"
          :stroke="isActive ? 'black' : 'gray-darker'"
        />
      </template>

      <template #text>
        {{ trans('user_menu.profile') }}
      </template>
    </PlusMenuLink>
  </li>

  <li>
    <PlusMenuLink :link="URL_USER_NOTIFICATIONS">
      <template #icon="{ isActive }">
        <Icon
          :svg="IconBellRinging"
          width="24"
          height="24"
          :fill="isActive ? 'black' : 'gray-darker'"
        />
      </template>

      <template #text>
        {{ trans('user_menu.notifications') }}
      </template>
    </PlusMenuLink>
  </li>

  <li class="px-3">
    <Divider class="m-0" color="gray" />
  </li>

  <li>
    <PlusMenuLink :link="URL_BUYER_INVOICES">
      <template #icon="{ isActive }">
        <Icon
          :svg="IconRent"
          width="24"
          height="24"
          :stroke="isActive ? 'black' : 'gray-darker'"
        />
      </template>

      <template #text>
        {{ trans('user_menu.invoices') }}
      </template>
    </PlusMenuLink>
  </li>

  <li class="px-3">
    <Divider class="m-0" color="gray" />
  </li>

  <li>
    <PlusMenuLink
      :link="URL_OFFER_CREATE"
    >
      <template #icon="{ isActive }">
        <Icon
          :svg="IconPlus"
          width="24"
          height="24"
          :fill="isActive ? 'black' : 'gray-darker'"
        />
      </template>

      <template #text>
        {{ trans('user_menu.main.create_offer') }}
      </template>
    </PlusMenuLink>
  </li>

  <li class="px-3">
    <Divider class="m-0" color="gray" />
  </li>
</template>
